import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Typography, Box, Chip, Alert, TextField, FormControl, InputLabel, Select, MenuItem, Paper,
  Card, CardContent, Grid
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import billingApi from '../../services/billingApi';
import { formatDate } from '../../utils/formatters';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

const ENTRY_TYPE_CONFIG = {
  credit:         { label: 'Credit',         color: 'success', sign: '-' },
  debit:          { label: 'Debit',          color: 'warning', sign: '+' },
  payment:        { label: 'Payment',        color: 'primary', sign: '' },
  refund:         { label: 'Refund',         color: 'error',   sign: '-' },
  tax:            { label: 'Tax',            color: 'info',    sign: '+' },
  fee:            { label: 'Fee',            color: 'secondary', sign: '-' },
  pending_change: { label: 'Pending Change', color: 'default', sign: '' }
};

function formatCents(cents) {
  if (cents === 0) return '$0.00';
  const abs = Math.abs(cents);
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${(abs / 100).toFixed(2)}`;
}

export default function FinancialLedger() {
  const columns = useMemo(() => [
    {
      field: 'created_at',
      headerName: 'Date',
      width: 180,
      type: 'date',
      renderCell: (row) => formatDate(row.created_at)
    },
    {
      field: 'entry_type',
      headerName: 'Type',
      width: 140,
      type: 'singleSelect',
      filterOptions: ['credit', 'debit', 'payment', 'refund', 'tax', 'fee', 'pending_change'],
      renderCell: (row) => {
        const config = ENTRY_TYPE_CONFIG[row.entry_type] || { label: row.entry_type, color: 'default' };
        return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
      }
    },
    {
      field: 'amount_cents',
      headerName: 'Amount',
      width: 120,
      type: 'numeric',
      renderCell: (row) => {
        const cents = row.amount_cents;
        const color = cents < 0 ? '#2D6A4F' : cents > 0 ? '#2C2B28' : '#5C5A55';
        return (
          <Typography variant="body2" sx={{ fontWeight: 600, color, fontFamily: "'Inter', monospace" }}>
            {formatCents(cents)}
          </Typography>
        );
      }
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 250
    },
    {
      field: 'stripe_invoice_id',
      headerName: 'Stripe Invoice',
      width: 160,
      renderCell: (row) => row.stripe_invoice_id ? (
        <Typography variant="caption" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", color: 'text.secondary' }}>
          {row.stripe_invoice_id.substring(0, 20)}...
        </Typography>
      ) : '—'
    },
    {
      field: 'customer_id',
      headerName: 'Customer',
      width: 140,
      renderCell: (row) => (
        <Typography variant="caption" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace" }}>
          {row.customer_id?.substring(0, 12)}...
        </Typography>
      )
    },
    {
      field: 'subscription_id',
      headerName: 'Subscription',
      width: 140,
      renderCell: (row) => row.subscription_id ? (
        <Typography variant="caption" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace" }}>
          {row.subscription_id.substring(0, 12)}...
        </Typography>
      ) : '—'
    }
  ], []);

  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ days: 30, customerId: '' });

  useEffect(() => {
    loadData();
  }, [filters.days]);

  async function loadData() {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({ limit: '500' });
      if (filters.days) params.set('days', String(filters.days));
      if (filters.customerId) params.set('customer_id', filters.customerId);

      const [ledgerData, summaryData] = await Promise.all([
        billingApi.get(`/api/admin/ledger?${params}`),
        billingApi.get(`/api/admin/ledger/summary?month=${new Date().toISOString().substring(0, 7)}`)
      ]);

      setEntries(ledgerData.entries || []);
      setSummary(summaryData.summary || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleFilterChange(key, value) {
    setFilters(prev => ({ ...prev, [key]: value }));
  }

  function handleSearchCustomer() {
    loadData();
  }

  if (loading && entries.length === 0) {
    return <AdminPageSkeleton />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Financial Ledger
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Payments
                </Typography>
                <Typography variant="h5" sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, color: 'primary.main' }}>
                  {formatCents(summary.total_payments)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Credits
                </Typography>
                <Typography variant="h5" sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, color: 'success.main' }}>
                  {formatCents(summary.total_credits)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Refunds
                </Typography>
                <Typography variant="h5" sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, color: 'error.main' }}>
                  {formatCents(summary.total_refunds)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ borderColor: 'primary.main', borderWidth: 2 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Net Revenue
                </Typography>
                <Typography variant="h5" sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 700 }}>
                  {formatCents(summary.net_revenue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={filters.days}
            onChange={(e) => handleFilterChange('days', e.target.value)}
            label="Period"
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
            <MenuItem value={365}>Last year</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Customer ID"
          placeholder="Filter by customer..."
          value={filters.customerId}
          onChange={(e) => handleFilterChange('customerId', e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearchCustomer()}
          sx={{ minWidth: 200 }}
        />

        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {entries.length} entries
        </Typography>
      </Paper>

      {/* Data Grid */}
      <Paper variant="outlined" sx={{ height: 600 }}>
        <AdminTable
          rows={entries}
          columns={columns}
          defaultSort={[{ field: 'created_at', direction: 'desc' }]}
        />
      </Paper>
    </Container>
  );
}
